import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bell } from "@phosphor-icons/react";
import { authenticatedApi } from "@/lib/api";

const TOKEN_STORAGE_KEY = "campodirecto.native.apns.token";
const INVITE_KEY = "campodirecto.push.invite.pending";
const safePushPath = /^(?:\/mensajes\/[A-Za-z0-9-]{1,100}|\/solicitudes|\/productor\/productos)$/;
const defaultPreferences = { messages: true, requests: true, transport: true, updates: true };

function pushPlugin() {
  // Never request permission in a normal browser or on an unsupported OS.
  if (typeof window === "undefined") return null;
  const bridge = window.Capacitor;
  return bridge?.getPlatform?.() === "ios" ? bridge?.Plugins?.PushNotifications || null : null;
}

export default function NativePushControl({ user }) {
  const currentUser = useRef(user?.id);
  currentUser.current = user?.id;
  const mounted = useRef(true);
  const [native, setNative] = useState(false);
  const [state, setState] = useState("idle");
  const [notice, setNotice] = useState("");
  const [open, setOpen] = useState(false);
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    setState("idle");
    setNotice("");
    setOpen(false);
    if (!user?.id || !pushPlugin()) return;
    void authenticatedApi.get("/push/preferences").then(({ data }) => {
      if (mounted.current && currentUser.current === user.id) setPreferences({
        messages: data.messages !== false, requests: data.requests !== false,
        transport: data.transport !== false, updates: data.updates !== false,
      });
    }).catch(() => {});
  }, [user?.id]);
  useEffect(() => {
    if (!native || !user?.id) return;
    const checkInvitation = () => {
      if (sessionStorage.getItem(INVITE_KEY) === String(user.id)) setOpen(true);
    };
    checkInvitation();
    window.addEventListener("campodirecto:signup", checkInvitation);
    return () => window.removeEventListener("campodirecto:signup", checkInvitation);
  }, [native, user?.id]);
  useEffect(() => {
    const plugin = pushPlugin();
    if (!plugin) return undefined;
    setNative(true);
    let handle, active = true;
    void Promise.resolve(plugin.addListener("pushNotificationActionPerformed", ({ notification }) => {
      const path = notification?.data?.path;
      if (active && currentUser.current && typeof path === "string" && safePushPath.test(path)) {
        window.location.assign(path);
      }
    })).then((subscription) => { if (active) handle = subscription; else void subscription.remove(); }).catch(() => {});
    return () => { active = false; if (handle) void handle.remove(); };
  }, []);

  function dismiss() {
    sessionStorage.removeItem(INVITE_KEY);
    setOpen(false);
    setNotice("");
  }
  async function savePreferences(next = preferences) {
    setSaving(true);
    try {
      const { data } = await authenticatedApi.put("/push/preferences", next);
      if (mounted.current) { setPreferences(data); setNotice("Preferencias guardadas."); }
    } catch {
      if (mounted.current) setNotice("No se pudieron guardar tus preferencias.");
    } finally { if (mounted.current) setSaving(false); }
  }
  async function enableNotifications() {
    const plugin = pushPlugin();
    if (!plugin || !user || state === "working") return;
    const requestedUserId = user.id;
    const stillCurrent = () => mounted.current && currentUser.current === requestedUserId;
    setState("working"); setNotice("");
    let successListener, errorListener;
    try {
      const status = await authenticatedApi.get("/push/status");
      if (status.data?.enabled !== true) throw new Error("unconfigured");
      if (!stillCurrent()) return;
      const permission = await plugin.requestPermissions();
      if (permission.receive !== "granted") throw new Error("denied");
      let resolveToken, rejectToken;
      const registration = new Promise((resolve, reject) => {
        resolveToken = resolve; rejectToken = reject;
      });
      successListener = await plugin.addListener("registration", ({ value }) => resolveToken(value));
      errorListener = await plugin.addListener("registrationError", () => rejectToken(new Error("registration")));
      await plugin.register();
      let timer;
      const token = await Promise.race([
        registration,
        new Promise((_, reject) => { timer = setTimeout(() => reject(new Error("timeout")), 15000); }),
      ]).finally(() => clearTimeout(timer));
      if (typeof token !== "string" || !/^[A-Fa-f0-9]{64}$/.test(token)) throw new Error("token");
      if (!stillCurrent()) return;
      await authenticatedApi.post("/push/register", { token, expected_user_id: requestedUserId });
      if (!stillCurrent()) return;
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      setState("enabled"); setNotice("Avisos del teléfono activados.");
      dismiss();
    } catch (error) {
      if (!stillCurrent()) return;
      setState("idle");
      setNotice(error?.message === "denied"
        ? "El permiso de notificaciones está desactivado en tu iPhone."
        : error?.message === "unconfigured"
          ? "Los avisos todavía no están disponibles para tu cuenta. Puedes activarlos más adelante."
          : "No se pudieron activar los avisos. Revisa la configuración del teléfono.");
    } finally {
      if (successListener) await successListener.remove().catch(() => {});
      if (errorListener) await errorListener.remove().catch(() => {});
    }
  }

  if (!user || !native) return null;
  return <>
    <button type="button" aria-label="Preferencias de notificaciones"
      title="Notificaciones del teléfono" onClick={() => setOpen(true)}
      className="min-h-11 min-w-11 inline-flex items-center justify-center rounded-xl border border-border">
      <Bell size={20} weight={state === "enabled" ? "fill" : "regular"} />
    </button>
    {notice && !open && <span role="status" className="sr-only">{notice}</span>}
    {open && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-5"
      onMouseDown={(event) => { if (event.target === event.currentTarget) dismiss(); }}>
      <section role="dialog" aria-modal="true" aria-labelledby="campo-push-title"
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl space-y-4">
        <h2 id="campo-push-title" className="text-xl font-bold text-foreground">Mantente al tanto</h2>
        <p className="text-sm text-muted-foreground">Activa los avisos del iPhone para enterarte de mensajes, respuestas a solicitudes, transporte y cambios de tus publicaciones o reportes. Puedes elegir qué recibir.</p>
        <fieldset className="space-y-3">
          <legend className="font-semibold text-sm mb-2">¿Qué avisos deseas recibir?</legend>
          {[
            ["messages", "Mensajes privados"],
            ["requests", "Respuestas a solicitudes y consultas de contacto"],
            ["transport", "Actualizaciones de transporte (cuando estén disponibles)"],
            ["updates", "Cambios en publicaciones y reportes"],
          ].map(([key, label]) => <label key={key} className="flex items-center gap-3 text-sm">
            <input type="checkbox" checked={preferences[key]} onChange={e => setPreferences(p => ({ ...p, [key]: e.target.checked }))} />
            <span>{label}</span>
          </label>)}
        </fieldset>
        <button type="button" className="btn-outline w-full" disabled={saving}
          onClick={() => void savePreferences()}>{saving ? "Guardando…" : "Guardar preferencias"}</button>
        <button type="button" className="btn-primary w-full" disabled={state === "working"}
          onClick={() => void enableNotifications()}>{state === "enabled" ? "Volver a registrar mi dispositivo" : state === "working" ? "Activando…" : "Activar notificaciones"}</button>
        {notice && <p role="status" className="text-sm">{notice}</p>}
        <button type="button" className="w-full py-2 text-sm font-medium" onClick={dismiss}>Ahora no</button>
      </section>
    </div>, document.body)}
  </>;
}
